import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Button, Icon, Tooltip, Modal, Badge } from '@syneto/compass-react';
import { useNavigate } from 'react-router-dom';
import Form from '../../../../components/forms/form';
import SelectOptionsRenderer from '../../../../helpers/SelectOptionsRenderer';
import { getActivationCode, getProductSuccess } from '../../../../data/product/actions';
import ProductAddon from './ProductAddon';
import Utils from '../../../../helpers/Utils';
import ProductMaintenancePlan from './ProductMaintenancePlan';
import { WarningModal } from './WarningModal';
import ProductValidationService from '../../validation/ProductValidationService';
import { applyAddonRowChange } from '../../../../helpers/addonPeriod';
import ExpiredProductAddons from './ExpiredProductAddons';
import { getSettingValue } from '../../../../helpers/settings';
import { getAssemblyTests } from '../../../../services/products';
import DateFormatterService from '../../../../helpers/DateFormatter';
import MaintenanceStatusFormatter from '../../../../helpers/MaintenanceStatusFormatter';
import PostInstallCheckLogsModal from '../../../../components/PostInstallCheckLogsModal';
import { getProductAddons } from '../../../../data/productAddons/actions';

const defaultAddon = () => {
	return {
		uuid: Utils.uuidv4(),
		addonId: '',
		duration: '',
		durationType: 'months',
		addonType: 'Time-based',
		startDate: null,
		startOnActivation: false,
		tierId: ''
	};
};

const ProductForm = (props) => {
	const {
		productAddons: availableProductAddons,
		getProductAddons,
		productModels,
		companiesWithEndUsers,
		maintenancePlansOptions,
		id,
		formName,
		isEditing,
		isSendingRequest,
		generatedActivationKey,
		getActivationCode,
		changeFormFieldInState,
		submit,
		handleSubmit,
		product,
		initialValues,
		maintenanceTotalDuration,
		maxMaintenanceExtension
	} = props;
	const [virtualAppliance, setVirtualAppliance] = useState(null);
	const [productAddons, setProductAddons] = useState(product?.productAddons ?? []);
	const [expiredProductAddons, setExpiredProductAddons] = useState([]);
	const [showExpiredAddons, setShowExpiredAddons] = useState(false);
	const [maintenanceServices, setMaintenanceServices] = useState(product?.maintenanceServices ?? initialValues.maintenanceServices);
	const [validationMessage, setValidationMessage] = useState(null);
	const [showAssemblyLogsModal, setShowAssemblyLogsModal] = useState(false);
	const [assemblyLogs, setAssemblyLogs] = useState('');
	const navigate = useNavigate();
	const expirationDate = (product?.activationDate && product?.maintenanceServices) ? DateFormatterService.formatDateToSpecificFormat(product?.maintenanceServices[0], 'expiresOn', 'DD/MM/YYYY') : null;


	const [activationDate, setActivationDate] = useState(product?.activationDate ?? null);
	
	function checkDateStatus(dateString) {
		const inputDate = moment(dateString, 'DD/MM/YYYY');
		if (!inputDate.isValid()) {
			return 'Invalid date format';
		}
		const today = moment().startOf('day');
		if (inputDate.isBefore(today)) {
			return 'Expired';
		} else {
			return 'Active';
		}
	}

	useEffect(() => {
		getProductAddons();
	}, []);

	useEffect(() => {
		if (product && product.assemblyTestStatus) {
			getAssemblyTests(product.serialNumber)
				.then((response) => {
					setAssemblyLogs(response.data[0].logs);
				})
				.catch((error) => {
					setAssemblyLogs('Failed to fetch assembly logs.');
				});
		}
	}, []);

	useEffect(() => {
		const hasCompute = availableProductAddons.find((addon) => { return addon.code === 'hasCompute'; });
		const hasStorage = availableProductAddons.find((addon) => { return addon.code === 'hasStorage'; });
		if (!hasCompute || !hasStorage) {
			return;
		}

		if (virtualAppliance) {
			changeFormFieldInState('virtualAppliance', true);
			if (!product) {
				changeFormFieldInState('serialNumber', null);
			}
			if (!product?.activationCode) {
				getActivationCode();
			}
			
			const addons = [...productAddons];
			if (!addons.some((addon) => { return addon.addonId === hasCompute.id; })) {
				addons.push({
					...defaultAddon(),
					addonId: hasCompute.id,
					startOnActivation: true
				});
			}
			if (!addons.some((addon) => { return addon.addonId === hasStorage.id; })) {
				addons.push({
					...defaultAddon(),
					addonId: hasStorage.id,
					startOnActivation: true
				});
			}
			if (addons.length !== productAddons.length) {
				setProductAddons(addons);
			}
		} else {
			changeFormFieldInState('virtualAppliance', false);
		}
	}, [virtualAppliance, availableProductAddons]);

	useEffect(() => {
		if (!product) {
			setProductAddons([]);
			setMaintenanceServices(initialValues.maintenanceServices);
			return;
		}

		const selectedProductModel = productModels.find((productModel) => { return productModel.id === product.modelId; });
		if (selectedProductModel) {
			setVirtualAppliance(selectedProductModel.virtualAppliance);
		}
		setProductAddons(product.productAddons);
		setMaintenanceServices(product.maintenanceServices);
	}, [productModels, product]);

	useEffect(() => {
		changeFormFieldInState('productAddons', productAddons);
		setExpiredProductAddons(productAddons.filter((addon) => { return new Date(addon.endDate) < new Date() && addon.isRenewed; }));
	}, [productAddons]);

	useEffect(() => {
		changeFormFieldInState('maintenanceServices', maintenanceServices);
	}, [maintenanceServices]);

	useEffect(() => {
		changeFormFieldInState('activationCode', generatedActivationKey);
	}, [generatedActivationKey]);

	// Addons
	const addAddon = useCallback(() => {
		setProductAddons([...productAddons, defaultAddon()]);
	}, [productAddons]);

	const changeAddon = useCallback((addon) => {
		setProductAddons(applyAddonRowChange(productAddons, addon));
	}, [productAddons]);

	const removeAddon = useCallback((uuid) => {
		setProductAddons(productAddons.filter((addon) => { return addon.uuid !== uuid; }));
	}, [productAddons]);

	// Maintenance services
	const defaultMaintenanceService = useCallback(() => {
		return {
			uuid: Utils.uuidv4(),
			maintenancePlanId: '',
			hasSerenity: false,
			duration: '',
			durationType: 'months',
			saleValue: '',
			isRenewal: true,
			quoteNumber: '',
		};
	}, []);

	const changeMaintenanceService = useCallback((maintenanceService, index) => {
		setMaintenanceServices(maintenanceServices.map((service) => { return service.uuid === index ? maintenanceService : service; }));
	}, [maintenanceServices]);

	const changeProductModel = useCallback((value) => {
		const selectedProductModel = productModels.find((productModel) => {
			return productModel.id === value;
		});

		if (selectedProductModel) {
			setVirtualAppliance(selectedProductModel.virtualAppliance);
		}
	}, [productModels]);

	const addMaintenanceService = useCallback(() => {
		setMaintenanceServices([...maintenanceServices, defaultMaintenanceService()]);
	}, [maintenanceServices]);

	const removeMaintenanceRenewal = useCallback((index) => {
		setMaintenanceServices(maintenanceServices.filter((service) => { return service.uuid !== index; }));
	}, [maintenanceServices]);

	const copyToClipboard = (inputId) => {
		const copyText = document.getElementById(inputId);
		copyText.select();
		document.execCommand('copy');
	};

	const renderAssemblyTestResults = (product) => {
		return product?.assemblyTestStatus ? (
			<>
				{renderPostInstallChecksSectionStatusPill(product.assemblyTestStatus)}
				<Button
					className="ms-2 py-0"
					role="tertiary"
					onClick={() => {
						return setShowAssemblyLogsModal(true);
					}}
				>
					<Icon className="me-2" name="fa fa-eye" color="#4f5ed7" /> View Logs
				</Button>
			</>
		) : (
			<span>Not performed for this machine</span>
		);
	};

	const renderPostInstallChecksSectionStatusPill = (status) => {
		return status === 'success' ? (
			<Tooltip text="Tests are successful" placement="top">
				<Badge appearance="success"><Icon name="fa fa-check" /></Badge>
			</Tooltip>
		) : (
			<Tooltip text="Tests have failed" placement="top">
				<Badge appearance="danger"><Icon name="fa fa-times" /></Badge>
			</Tooltip>
		);
	};

	const isFormValid = useCallback((product) => {
		if (product.maintenanceServices.length > 1) {
			if (!ProductValidationService.isSerialNumberValid(product.serialNumber) && !ProductValidationService.isDurationValid(maxMaintenanceExtension, maintenanceTotalDuration)) {
				setValidationMessage('That doesn\'t look like a correct serial number and the maximum number of years has been exceeded. Continue?');
				return false;
			}
			if (!ProductValidationService.isDurationValid(maxMaintenanceExtension, maintenanceTotalDuration)) {
				setValidationMessage('Maximum number of years has been reached. Continue?');
				return false;
			}
		} else if (!ProductValidationService.isSerialNumberValid(product.serialNumber) && !product.virtualAppliance) {
			setValidationMessage('That doesn\'t look like a correct serial number. Continue?');
			return false;
		}

		return true;
	}, [maintenanceTotalDuration, maxMaintenanceExtension]);

	const trySubmit = useCallback((product) => {
		if (isFormValid(product)) {
			return submit(product);
		}
	}, []);

	const submitWarningModal = useCallback((product) => {
		submit(product);
		setValidationMessage(null);
	}, []);

	return (
		<>
			<form id={id} className="form" onSubmit={handleSubmit(trySubmit)}>
				<fieldset className="border-0">
					<Row>
						<Col xs={8}>
							<h4 className="mt-4">Appliance</h4>
							<Row>
								<Col xs={3}>
									<Form.select
										label="Model"
										name="modelId"
										options={SelectOptionsRenderer.renderProductModel(productModels)}
										onValueChange={changeProductModel}
										emptyOption="Please select"
										required
									/>
								</Col>
								<Col xs={3}>
									<Form.text
										name="serialNumber"
										label="Serial number"
										disabled={virtualAppliance}
										required={!virtualAppliance}
										placeholder={virtualAppliance ? 'No serial attached' : ''}
									/>
								</Col>
								<Col xs={3}>
									<Form.text name="saleValue" label="Sale value" />
								</Col>
								<Col xs={3}>
									<Form.text name="osVersion" label="OS Version" disabled />
								</Col>
							</Row>

							<hr />
							{expiredProductAddons.length > 0 && <Button role="tertiary" className="float-end" onClick={() => { setShowExpiredAddons(true); }}>See expired add-ons</Button>}
							<h4 className="mt-4">Add-ons</h4>
							{productAddons.filter((addon) => { return !addon.isRenewed; }).length > 0 && <Row className="fw-bold mb-2">
								<Col xs={2}>Add-on name*</Col>
								<Col xs={1} className="text-center">Type</Col>
								<Col xs={2}>Start date</Col>
								<Col xs={2}>Duration*</Col>
								<Col xs={2}>End date</Col>
								<Col xs={1}>Tier</Col>
								<Col>Start on activation</Col>
							</Row>}
							{productAddons.map((addon, index) => {
								if (addon.isRenewed && addon.isExpired) {
									return null; // Skip renewed add-ons
								}

								return (
									<ProductAddon key={addon.uuid} index={index} addon={addon} removeAddon={removeAddon} changeAddon={changeAddon} changeProp={changeFormFieldInState} />
								);
							})}
							<Row>
								<Col xs={'auto'} className="pt-2 add-note-btn-support" style={{ fontSize: '14px' }} onClick={addAddon}>
									<i className="fa fa-plus me-2" />Add new add-on
								</Col>
							</Row>

							{product && <Row>
								<label className="col-auto mt-4 pt-2"><strong>Assembly test results:</strong></label>
								<div className="col-8 mt-4 pt-2">
									{renderAssemblyTestResults(product)}
								</div>
							</Row>}

							<Row className="mt-3">
								<Col xs={12}>
									<Form.textarea label={virtualAppliance ? 'Notes*' : 'Notes'} id="notes" name="notes" />
								</Col>
							</Row>

							<hr />
							<h4 className="mt-4">Maintenance</h4>

							{!isEditing && <ProductMaintenancePlan
								index={0}
								maintenancePlansOptions={maintenancePlansOptions}
								maintenanceService={maintenanceServices[0]}
								isRenewal={false}
								change={changeFormFieldInState}
								changeMaintenanceService={changeMaintenanceService}
							/>}

							{isEditing && maintenanceServices.map((maintenanceService, index) => {
								return (
									<ProductMaintenancePlan
										key={maintenanceService.uuid}
										index={index}
										maintenancePlansOptions={maintenancePlansOptions}
										maintenanceService={maintenanceService}
										isRenewal={index > 0}
										change={changeFormFieldInState}
										changeMaintenanceService={changeMaintenanceService}
										deleteMaintenanceService={removeMaintenanceRenewal}
									/>
								);
							})}
							{isEditing && maintenanceServices && <Row>
								<Button role="tertiary" onClick={addMaintenanceService}>Extend maintenance</Button>
							</Row>}
						</Col>
						<Col xs={4}>
							<Row className="d-flex align-items-start justify-content-between mt-4">
								<h4 className="col-auto">Activation details</h4>
								{
									product?.activationDate ?
										(checkDateStatus(expirationDate) !== 'Expired' ?
											<Badge appearance={MaintenanceStatusFormatter.setExpiresOnBadge('Active')}>
												{`Expires on: ${expirationDate}`}
											</Badge> :
											<Badge appearance={MaintenanceStatusFormatter.setExpiresOnBadge('Expired')}>
												{`Expired since: ${expirationDate}`}
											</Badge>)
										: null
								}
							</Row>
							<Row>
								<Col xs={12}>
									<Form.select
										label="End-user"
										name="userId"
										options={SelectOptionsRenderer.renderCompaniesWithUsers(companiesWithEndUsers)}
										emptyOption="Not set"
										disabled={virtualAppliance}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={12}>
									<div className="mb-3">
										<Form.datepicker
											formName={formName}
											calendarClassName="activation-details__date-picker"
											id="activationDate"
											name="activationDate"
											label="Activation date"
											placeholderText="Not set"
											placement="left"
											initValue={activationDate ? activationDate : null}
											autoComplete="off"
											disabled={virtualAppliance && !isEditing}
											onValueChange={(newDate) => {
												setActivationDate(newDate ? moment(newDate) : null);
											}}
										/>
									</div>
								</Col>
							</Row>
							{virtualAppliance && <Row>
								<Col xs={12}>
									<div className="float-end">
										<Tooltip text="Copy activation key" placement="top" trigger="click">
											<Button className="p-0 me-2" role="tertiary" onClick={() => { return copyToClipboard('activationCode'); }}>
												<Icon name="fa fa-copy" color="black" />
											</Button>
										</Tooltip>
										{!isEditing && <Button className="p-0 me-2" role="tertiary" onClick={() => { return getActivationCode(); }}>
											<Icon name="fa fa-sync" color="black" />
										</Button>}
									</div>
									<Form.text
										id="activationCode"
										name="activationCode"
										label="Activation Code"
										value={product?.activationCode ?? ''}
										placeholder="Not set"
										readOnly
									/>
								</Col>
							</Row>}
						</Col>
					</Row>
					<Row className="justify-content-end">
						<Col xs={'auto'} className="p-0">
							<Button role="secondary" onClick={() => { navigate('/maintenance-service/maintenance-plans'); }}>Exit without saving</Button>
						</Col>
						<Col xs={'auto'}>
							<Button disabled={isSendingRequest} type="submit">
								{isSendingRequest ? <Icon name="fa fa-spinner fa-spin" /> : isEditing ? 'Save details' : 'Create'}
							</Button>
						</Col>
					</Row>
				</fieldset>
			</form>

			<PostInstallCheckLogsModal
				show={showAssemblyLogsModal}
				onHide={() => {
					return setShowAssemblyLogsModal(false);
				}}
				userRole='operations'
				assemblyLogs={assemblyLogs}
				serialNumber={product?.serialNumber}
			/>

			<WarningModal
				showWarningModal={!!validationMessage}
				content={validationMessage}
				closeWarningModal={() => { setValidationMessage(null); }}
				onSubmit={handleSubmit(submitWarningModal)}
			/>

			<Modal
				enforceFocus={false}
				show={showExpiredAddons}
				onHide={() => { setShowExpiredAddons(false); }}
				content={<ExpiredProductAddons addons={expiredProductAddons} />}
				title="Expired add-ons"
				size="lg"
			/>
		</>);
};

const mapStateToProps = (state) => {
	const selectedProduct = state.products.selectedProduct;
	const maintenanceTotalDuration = selectedProduct?.maintenanceServices?.slice(1).reduce(
		(acc, maintenanceExtension) => {
			acc += parseInt(maintenanceExtension.duration);
			return acc;
		},
		parseInt(selectedProduct?.maintenanceServices[0].duration)
	);

	return {
		generatedActivationKey: state.products.activationKey,
		productAddons: state.productAddons.data,
		productModels: state.productModels.models,
		companiesWithEndUsers: state.companies.companiesWithEndUsers,
		maintenancePlansOptions: state.maintenancePlansOptions.maintenancePlansOptions.allMaintenancePlansOptions,
		maintenanceTotalDuration: maintenanceTotalDuration,
		maxMaintenanceExtension: getSettingValue(state.settings.data, 'maxMaintenanceExtension') ?? 36,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getActivationCode: () => { return dispatch(getActivationCode()); },
		setSelectedProduct: (data) => { return dispatch(getProductSuccess(data)); },
		getProductAddons: () => { return dispatch(getProductAddons()); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductForm);

ProductForm.propTypes = {
	productModels: PropTypes.array,
	companiesWithEndUsers: PropTypes.array,
	maintenancePlansOptions: PropTypes.array,
	id: PropTypes.string.isRequired,
	formName: PropTypes.string.isRequired,
	isEditing: PropTypes.bool.isRequired,
	isSendingRequest: PropTypes.bool,
	getActivationCode: PropTypes.func.isRequired,
	changeFormFieldInState: PropTypes.func.isRequired,
	submit: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	product: PropTypes.object,
	initialValues: PropTypes.object
};
